import type { TargetMelody } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import { buildMelodyPreview } from './melodyPreviewGeometry';

/**
 * MelodyPreview(#11) — 준비 단계에서 부르기 전에 전체 음계를 미리 보여준다. 목표
 * 노트를 음높이×시간 그리드에 계단식으로 깔고(노트마다 계이름/음이름 + lyric),
 * 음역 요약과 편의 옥타브 안내를 함께 낸다. 좌표는 라이브 뷰(#8)와 같은 순수
 * `buildScoreOverlay` — 시작하면 이 위에 플레이헤드+실제 음정이 그대로 얹힌다.
 * 스코어링은 octaveTolerant(기본)라 편한 옥타브로 불러도 in-tune으로 잡힌다.
 *
 * 카운트다운(#14) 중에도 같은 그림을 계속 렌더하고 그 위에 3·2·1 숫자를
 * 오버레이한다(`countdown` prop). 오버레이는 pointer-events 없이 미리보기를
 * 흐리지 않게 얹히고, 마지막 초(`highlightFirstNote`)엔 첫 목표 음을 강조한다.
 */
export const MelodyPreview = ({
  melody,
  countdown = null,
  highlightFirstNote = false,
}: {
  melody: TargetMelody;
  countdown?: number | null;
  highlightFirstNote?: boolean;
}) => {
  const { overlay, labels, rangeLabel } = buildMelodyPreview(melody);
  const { width, height, targetLines } = overlay;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
          시작 전 음계 미리보기
          <InfoTip label="음계 미리보기 설명">
            부르기 전에 이 곡의 목표 음을 음높이×시간 그리드로 미리 보여줍니다.
            시작하면 같은 좌표계 위에 현재 시각(플레이헤드)과 실제 음정이
            얹힙니다.
          </InfoTip>
        </span>
        <span className="font-mono text-[11px] text-muted-foreground">
          이 곡: {rangeLabel}
        </span>
      </div>

      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="h-40 w-full rounded-md border border-border bg-muted/20"
          role="img"
          aria-label={`목표 음계 미리보기 (${rangeLabel})`}
        >
          {targetLines.map((line, i) => {
            const isFirst = highlightFirstNote && i === 0;
            return (
              <line
                key={`p-${i}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                style={{
                  stroke: isFirst ? 'hsl(var(--primary))' : 'hsl(var(--ring))',
                }}
                strokeWidth={isFirst ? 6 : 4}
                strokeLinecap="round"
                opacity={isFirst ? 1 : 0.6}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>

        {labels.map((l, i) => {
          const isFirst = highlightFirstNote && i === 0;
          return (
            <span
              key={`l-${i}`}
              className={`pointer-events-none absolute -translate-x-1/2 -translate-y-[140%] whitespace-nowrap text-center font-mono text-[10px] leading-tight text-foreground${
                isFirst ? ' scale-125 font-bold' : ''
              }`}
              style={{
                left: `${(l.x / width) * 100}%`,
                top: `${(l.y / height) * 100}%`,
              }}
            >
              <span className="text-primary">{l.solfege}</span>
              <span className="ml-0.5 text-muted-foreground">{l.noteName}</span>
              {l.lyric && (
                <span className="block text-[9px] text-muted-foreground/80">
                  “{l.lyric}”
                </span>
              )}
            </span>
          );
        })}

        {countdown != null && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1">
            <span className="text-[11px] font-medium text-muted-foreground [text-shadow:0_1px_3px_hsl(var(--background))]">
              곧 시작합니다
            </span>
            <span className="font-mono text-7xl font-bold tabular-nums text-primary [text-shadow:0_2px_10px_hsl(var(--background)),0_0_4px_hsl(var(--background))]">
              {countdown}
            </span>
          </div>
        )}
      </div>

      <p className="rounded-md bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
        편한 옥타브로 불러도 됩니다 — 옥타브가 달라도 같은 계이름이면 정확으로
        잡힙니다.
      </p>
    </div>
  );
};
